<script setup lang="ts">
const route = useRoute();
const { siteUrl } = useRuntimeConfig().public;

const { data } = await useKirbyData(route.path, {
  transform: (page: Record<string, any>) => slimPagePayload(page),
});
requirePageData(data.value);

// Set the current page data for the global page context
setPage(data.value);

const layoutFaqs = computed(() =>
  extractFaqsFromLayouts(data.value?.layouts),
);

const pageFaqs = computed(() => {
  if (Array.isArray(data.value?.faqs) && data.value.faqs.length > 0) {
    return data.value.faqs as {
      question?: string;
      answer?: string;
      title?: string;
      text1?: string;
      categories?: string;
    }[];
  }
  return layoutFaqs.value;
});

/** Group FAQs by category for inline (visible) accordion rendering. */
const groupedFaqs = computed(() => {
  const cats = Array.isArray(data.value?.faqCats) ? data.value.faqCats : [];
  const list = pageFaqs.value;
  const sanitize = (str: string) =>
    String(str ?? "")
      .toLowerCase()
      .replace(/['`']/g, "")
      .replace(/[^\w\s]/g, "")
      .trim();

  if (!cats.length) {
    const flat = flattenFaqItems({ faqs: list as never });
    return flat.length
      ? { FAQs: { faqs: flat, catIcon: "" } }
      : ({} as Record<string, { faqs: any[]; catIcon: string }>);
  }

  const grouped: Record<string, { faqs: any[]; catIcon: string }> = {};
  for (const cat of cats) {
    const category = String(cat?.category ?? "").trim();
    if (!category) continue;
    const matched = list.filter((item: any) =>
      sanitize(item.categories).includes(sanitize(category)),
    );
    grouped[category] = {
      faqs: matched,
      catIcon: cat.catIcon ?? "",
    };
  }
  return grouped;
});

const visibleFaqItems = computed(() =>
  flattenFaqItems({
    faqs: Object.values(groupedFaqs.value).flatMap((g) => g.faqs) as never,
  }),
);

useJsonLdHead(
  computed(() => {
    if (!data.value) return null;

    // Only emit FAQPage when Q&As are actually rendered inline (not modal-only).
    const faqs = visibleFaqItems.value;
    const primary = faqs.length
      ? buildFaqPageJsonLd(data.value, route.path, siteUrl, faqs)
      : buildWebPageJsonLd(data.value, route.path, siteUrl);

    return withBreadcrumbJsonLd(primary, route.path, siteUrl);
  }),
);

useHead({
  bodyAttrs: {
    class: `${data.value?.template}-page`,
  },
});

definePageMeta({
  layout: "default",
});
const firstPartOfPath = route.path.split("/")[1];

const showQuizModal = ref(false);
const toggleQuizModal = () => {
  showQuizModal.value = !showQuizModal.value;
};

const closeQuizModal = () => {
  showQuizModal.value = false;
};
</script>

<template>
  <div>
    <Teleport to="body">
      <Transition name="pop" appear>
        <div v-if="showQuizModal" v-cloak class="modal quiz-modal">
          <AppFitnessQuiz />
        </div>
      </Transition>
      <Transition name="fade" appear>
        <div
          v-if="showQuizModal"
          v-cloak
          class="backdrop"
          @click="closeQuizModal"
        ></div>
      </Transition>
    </Teleport>
    <AppHeading1 />
    <AppHeroImage v-if="data?.heroType === 'Hero Image'" />
    <AppHeroVideo v-if="data?.heroType === 'Hero Video'" />
    <div class="content-wrapper">
      <!-- Template: fitness-preparation -->
      <div v-if="data?.template === 'fitness-preparation'">
        <section class="page-intro-section">
          <AppNavBreadcrumb />
          <KirbyLayouts v-if="data?.layouts" :layouts="data?.layouts ?? []" />
        </section>

        <section class="content">
          <AppFitnessTips />
        </section>

        <section class="case-study-section">
          <section class="content">
            <AppFitnessCaseStudies />
          </section>
        </section>

        <section id="quizGrading">
          <section class="content">
            <div class="btn-wrap">
              <div class="quiz-btn">
                <h2>Fitness Quiz</h2>
                <p>How fit are you? If you haven't already done so...</p>
                <NuxtLink class="btn" @click.prevent="toggleQuizModal">
                  TAKE OUR FITNESS QUIZ
                </NuxtLink>
              </div>
              <div class="image">
                <NuxtPicture
                  format="webp"
                  loading="lazy"
                  src="/images/hikers.jpg"
                  width="400"
                  height="250"
                  alt="Hikers"
                  sizes="sm:200px md:300px lg:400px"
                />
              </div>
              <div class="grading-btn">
                <h2>How we Grade Treks</h2>
                <p>Deep dive into our trek grading</p>
                <NuxtLink :to="`/${firstPartOfPath}/trek-grading`" class="btn">
                  Trek Grading
                </NuxtLink>
              </div>
            </div>
          </section>
        </section>

        <section class="content">
          <AppNutrition />
        </section>
      </div>

      <!-- Template: where-to-stay -->
      <div v-if="data?.template === 'where-to-stay'">
        <div class="content">
          <AppNavBreadcrumb />
          <KirbyLayouts :layouts="data?.layouts ?? []" />

          <AppAccommodationAll />
          <section v-if="data?.parentPageTemplate === 'treks'">
            <div id="trekUsefulInfo">
              <AppTrekUsefulInfo
                :trek-page="data?.trekPage"
                :trek-title="data?.parentPageTitle"
              />
            </div>
          </section>
        </div>
      </div>

      <!-- Template: what-to-bring -->
      <div v-if="data?.template === 'what-to-bring'">
        <div class="content">
          <AppNavBreadcrumb />
          <KirbyLayouts :layouts="data?.layouts ?? []" />
          <AppKitList />
          <section v-if="data?.parentPageTemplate === 'treks'">
            <div id="trekUsefulInfo">
              <AppTrekUsefulInfo
                :trek-page="data?.trekPage"
                :trek-title="data?.parentPageTitle"
              />
            </div>
          </section>
        </div>
      </div>

      <!-- Template: faqs — inline accordion so Q&As are in HTML (not modal-only) -->
      <div v-if="data?.template === 'faqs'">
        <div class="content">
          <AppNavBreadcrumb />
          <KirbyLayouts :layouts="data?.layouts ?? []" />
          <AppTrekFaqs :faqs="groupedFaqs" />
        </div>
      </div>

      <!-- Template: bag-transfer -->
      <div v-if="data?.template === 'bag-transfer'">
        <div class="content">
          <AppNavBreadcrumb />

          <KirbyLayouts v-if="data?.layouts" :layouts="data?.layouts ?? []" />
          <AppBagTransfer />
        </div>
      </div>

      <!-- Template: trek-grading -->
      <div v-if="data?.template === 'trek-grading'">
        <section class="page-intro-section">
          <div class="container">
            <AppNavBreadcrumb />
            <KirbyLayouts v-if="data?.layouts" :layouts="data?.layouts ?? []" />
            <h4>Our trek grading considers:</h4>
            <AppTrekGradingConsiderations />
          </div>
        </section>
        <AppTrekGrades />
      </div>

      <!-- Template: trekking-accommodation -->
      <div v-if="data?.template === 'trekking-accommodation'">
        <div class="content">
          <AppNavBreadcrumb />
          <KirbyLayouts :layouts="data?.layouts ?? []" />
        </div>
      </div>

      <!-- Template: default -->
      <div v-if="data?.template === 'default' || data?.template === 'page'">
        <div class="content cols">
          <div class="main-col">
            <AppNavBreadcrumb />
            <KirbyLayouts :layouts="data?.layouts ?? []" />
          </div>
          <div id="infoNavPage" class="right-col">
            <AppInfoNav />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
